export type Phase =
  | "idle"
  | "arming"
  | "recording"
  | "paused"
  | "finalizing"
  | "saved"
  | "failed";

export type PipShape = "circle" | "rounded" | "square";

export interface PipLayout {
  /** Fraction of canvas width/height, top-left of the PiP box. */
  x: number;
  y: number;
  /** Height as a fraction of canvas height. Width follows the shape's aspect. */
  size: number;
  shape: PipShape;
  mirrored: boolean;
}

export interface ScreenEntry {
  id: string;
  label: string;
  stream: MediaStream;
  hasSystemAudio: boolean;
}

export interface Take {
  id: string;
  name: string;
  fileName: string;
  mime: string;
  bytes: number;
  durationMs: number;
  width: number;
  height: number;
  createdAt: number;
  thumb: string | null;
  /** Present when the file lives in a folder the user granted. */
  folderName: string | null;
  /** Structured-cloneable handle; re-permissioned on read. */
  fileHandle: FileSystemFileHandle | null;
  /** Set when the take was rescued from the journal after an unclean exit. */
  recovered: boolean;
}

export interface QualitySettings {
  height: 720 | 1080 | 1440;
  fps: 30 | 60;
}

export interface AppSettings {
  quality: QualitySettings;
  pip: PipLayout;
  cameraOn: boolean;
  micOn: boolean;
  systemAudioOn: boolean;
  countdownSeconds: 0 | 3 | 5;
  folderName: string | null;
  theme: "auto" | "light" | "dark";
}

export const DEFAULT_PIP: PipLayout = {
  x: 0.7,
  y: 0.66,
  size: 0.22,
  shape: "circle",
  mirrored: true,
};

export const DEFAULT_SETTINGS: AppSettings = {
  quality: { height: 1080, fps: 30 },
  pip: { ...DEFAULT_PIP },
  cameraOn: true,
  micOn: true,
  systemAudioOn: false,
  countdownSeconds: 3,
  folderName: null,
  theme: "auto",
};

export function canvasSizeFor(height: number): { width: number; height: number } {
  const w = Math.round((height * 16) / 9 / 2) * 2;
  return { width: w, height: Math.round(height / 2) * 2 };
}

export function videoBitrateFor(width: number, height: number, fps: number): number {
  // ~0.12 bit/s per pixel per frame: 1080p30 ≈ 7.4 Mbps, capped for 1440p60.
  const raw = width * height * fps * 0.12;
  return Math.min(Math.max(Math.round(raw), 2_500_000), 24_000_000);
}
